<template>
  <!-- form -->
  <div>
    <Modal
      :id="modalOptions.clickedId"
      ref="confirmModal"
      :text="modalOptions.text"
      :ok-button="modalOptions.okButton"
      :cancel-button="modalOptions.cancelButton"
      :show="show"
      @result="modalResult"
      @hidden="show = false"
    />
    <div class="card">
      <div class="card-header">
        <h3>{{ $t(formTitle) }}</h3>
      </div>
      <div class="card-body">
        <div class="activity-form-help">
          <p>{{ $t('activity_help_1') }}</p>
          <small v-if="!userCan($store.state.auth.user.plan, 'widget')">{{
            $t('activity_help_embed')
          }}</small>
          <br />
        </div>
        <validation-observer ref="simpleRules">
          <b-form>
            <b-row>
              <BCol md="6">
                <BRow>
                  <b-col md="12">
                    <b-form-group label-for="name" :label="$t(typeName + ' name')">
                      <validation-provider
                        #default="{ errors }"
                        :name="$t(typeName + ' name')"
                        rules="required"
                      >
                        <b-form-input
                          id="name"
                          v-model="formData.name"
                          :state="errors.length > 0 ? false : null"
                          :placeholder="$t(typeName + ' name')"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col md="12" class="description-col">
                    <b-form-group label-for="details" :label="$t('Description')">
                      <div class="form-help">
                        <feather-icon
                          id="desc_help"
                          icon="HelpCircleIcon"
                          class="icon-button"
                          size="18"
                        />
                        <b-tooltip target="desc_help" triggers="hover">
                          {{ $t('activity_desc_help') }}
                        </b-tooltip>
                      </div>
                      <validation-provider
                        #default="{ errors }"
                        rules="required"
                        :name="$t('Description')"
                      >
                        <quill-editor
                          id="details"
                          v-model="formData.details"
                          :options="editorOptions"
                          :state="errors.length > 0 ? false : null"
                          :class="errors.length > 0 ? 'border-danger-for-text-editor' : ''"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <!-- keywords -->
                  <b-col v-if="userCan($store.state.auth.user.plan, 'serp_priority')" cols="12">
                    <b-form-group label-for="keywords" :label="$t('Keywords')">
                      <div class="form-help">
                        <feather-icon
                          id="keyword_help"
                          icon="HelpCircleIcon"
                          class="icon-button"
                          size="18"
                        />
                        <b-tooltip target="keyword_help" triggers="hover">
                          {{ $t('keyword_help') }}
                        </b-tooltip>
                      </div>
                      <b-form-input
                        id="keywords"
                        v-model="formData.keywords"
                        :placeholder="$t('...')"
                      />
                    </b-form-group>
                    <hr />
                  </b-col>
                  <b-col md="12">
                    <b-form-group label-for="from_date" :label="$t('Start date')">
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('Start date')"
                        rules="required"
                      >
                        <date-picker
                          v-model="formData.from_date"
                          format="DD/MM/YYYY"
                          type="date"
                          :lang="{
                            formatLocale: {
                              firstDayOfWeek: 1,
                            },
                            monthBeforeYear: false,
                          }"
                          :disabled-date="disableDateAfter"
                          class="w-100"
                          :class="errors.length > 0 ? 'border-danger-for-time-picker' : ''"
                          value-type="format"
                          :placeholder="$t('Start date')"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col md="12">
                    <b-form-group label-for="to_date" :label="$t('End date')">
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('End date')"
                        rules="required"
                      >
                        <date-picker
                          v-model="formData.to_date"
                          format="DD/MM/YYYY"
                          type="date"
                          class="w-100"
                          :lang="{
                            formatLocale: {
                              firstDayOfWeek: 1,
                            },
                            monthBeforeYear: false,
                          }"
                          :class="errors.length > 0 ? 'border-danger-for-time-picker' : ''"
                          value-type="format"
                          :disabled-date="disableDateBefore"
                          :placeholder="$t('End date')"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col md="12">
                    <b-form-group label-for="class-type" :label="$t('Class type')">
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('Class type')"
                        rules="required"
                      >
                        <v-select
                          id="class-type"
                          v-model="formData.category_options"
                          multiple
                          :loading="classTypeLoader"
                          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                          :placeholder="$t('Class type')"
                          label="name"
                          :options="classTypeList"
                          :reduce="option => option.id"
                          :class="errors.length > 0 ? 'border-danger-for-v-select' : ''"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <b-col md="12">
                    <b-form-group label-for="age" :label="$t('Age')">
                      <validation-provider #default="{ errors }" :name="$t('Age')" rules="required">
                        <vue-slider
                          id="age"
                          v-model="ageRange"
                          class="mb-2 mt-3"
                          :min="0"
                          :max="19"
                          :marks="marks"
                          :included="true"
                          :hide-label="true"
                          :process-style="{ backgroundColor: '#6BC4C5' }"
                          :tooltip-style="{
                            backgroundColor: '#F8F8F8',
                            borderColor: '#F8F8F8',
                            color: '#0E1A1A',
                            fontWeight: '700',
                          }"
                          :tooltip="'always'"
                          :state="errors.length > 0 ? false : null"
                        >
                          <template #tooltip="{ index, value }">
                            <div v-if="value === 0.5" class="tooltip-slider">
                              6 {{ $t('months') }}
                            </div>
                            <div v-if="value < 0.5" class="tooltip-slider">
                              {{ $t('New born') }}
                            </div>
                            <div v-if="value >= 1 && value < 19" class="tooltip-slider">
                              {{ value }}
                              {{ value === 1 ? $t('year') : $t('years') }}
                            </div>
                            <div v-if="value == 19" class="tooltip-slider">
                              {{ $t('Adults') }}
                            </div>
                          </template>
                        </vue-slider>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col md="12">
                    <b-form-group label-for="language" :label="$t('Language')">
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('Language')"
                        rules="required"
                      >
                        <v-select
                          id="language"
                          v-model="formData.language_options"
                          multiple
                          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                          :placeholder="$t('Language')"
                          label="language"
                          :loading="locationLoader"
                          :options="languagesList"
                          :reduce="option => option.id"
                          :class="errors.length > 0 ? 'border-danger-for-v-select' : ''"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col md="12">
                    <b-form-group label-for="location" :label="$t('Location')">
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('Location')"
                        rules="required"
                      >
                        <v-select
                          id="location"
                          v-model="formData.location_id"
                          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                          :placeholder="$t('Location')"
                          label="name"
                          :loading="locationLoader"
                          :options="locationsList"
                          :reduce="option => option.id"
                          :class="errors.length > 0 ? 'border-danger-for-v-select' : ''"
                          :state="errors.length > 0 ? false : null"
                        >
                          <template #list-footer>
                            <li class="text-center d-flex">
                              <b-link
                                class="cursor-pointer"
                                style="margin: auto; padding: 0.3rem; width: 100%"
                                :to="{ name: 'create-location' }"
                              >
                                {{ $t('Add new location') }}
                              </b-link>
                            </li>
                          </template>
                        </v-select>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </BRow>
              </BCol>

              <BCol md="6">
                <b-col md="12">
                  <b-form-group
                    id="check-schedule-options"
                    label-for="days"
                    class="option-label"
                    :label="$t('Schedule options')"
                  >
                    <validation-provider
                      #default="{ errors }"
                      :name="$t('Schedule options')"
                      rules="required"
                    >
                      <b-form-select
                        id="days"
                        v-model="formData.days"
                        class="d-none"
                        :state="errors.length > 0 ? false : null"
                        :placeholder="$t('Schedule options')"
                      />
                      <ActivityScheduleOptions
                        v-for="(option, i) in formData.days"
                        :id="'ScheduleOption' + i"
                        :key="i"
                        :ref="'ScheduleOption' + i"
                        :days-list="daysList"
                        :options-length="formData.days.length"
                        label="Schedule option"
                        :value="option"
                        :option-index="i"
                        @optionChanged="changeScheduleData($event, option)"
                        @deleteOption="deleteScheduleOption"
                      />
                      <small class="text-danger d-flex mb-1" style="margin-top: -1rem">{{
                        errors[0]
                      }}</small>
                      <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        class="ml-auto mb-1"
                        variant="outline-primary"
                        @click="addNewScheduleOption"
                      >
                        + {{ $t('Add new schedule option') }}
                      </b-button>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col md="12">
                  <hr />

                  <b-form-group v-if="userCan($store.state.auth.user.plan, 'payments')">
                    <b-form-checkbox
                      id="billable"
                      v-model="formData.billable"
                      name="billable"
                      value="1"
                      style="max-width: 80%"
                      unchecked-value="0"
                      switch
                    >
                      {{ $t('Billable activity') }}
                    </b-form-checkbox>
                    <div class="form-help">
                      <feather-icon
                        id="billable_help"
                        icon="HelpCircleIcon"
                        class="icon-button"
                        size="18"
                      />
                      <b-tooltip target="billable_help" triggers="hover">
                        {{ $t('billable_help') }}
                      </b-tooltip>
                    </div>
                  </b-form-group>
                  <b-form-group
                    id="check-price-options"
                    label-for="payment-options"
                    class="option-label"
                    :label="$t('Payment options')"
                  >
                    <PriceOptions
                      v-for="(option, i) in formData.price_options"
                      :id="'priceOption' + i"
                      :key="i"
                      :ref="'priceOption' + i"
                      :type="typeId"
                      :billable="!!formData.billable"
                      label="Payment option"
                      :value="option"
                      :option-index="i"
                      @optionChanged="changePriceData($event, option)"
                      @deleteOption="deletePriceOption"
                    />
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      class="ml-auto mb-1"
                      variant="outline-primary"
                      @click="addNewPriceOption"
                    >
                      + {{ $t('Add new payment option') }}
                    </b-button>
                  </b-form-group>
                </b-col>

                <DropZone
                  :label="'Add photos (max 10)'"
                  :files-thumbnail="formData.filesThumbnail"
                  :files-i-ds="formData.files"
                  @data-changed="changeFilesIDs"
                />
                <validation-provider #default="{ errors }" name="photos" rules="required">
                  <b-form-input id="" v-model="filesInput" class="d-none" placeholder="Photo" />
                  <small class="text-danger d-flex mb-1" style="margin-top: -1rem">{{
                    errors[0]
                  }}</small>
                  <span class="files-help">
                    <p>{{ $t('files-help-3') }}</p>
                    <p>{{ $t('files-help-2') }}</p>
                    <p>{{ $t('files-help-4') }}</p>
                  </span>
                </validation-provider>
              </BCol>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="warning"
                  type="submit"
                  class="mr-2 mt-1 draft-button"
                  :disabled="submitLoader || !hasChanged"
                  @click.prevent="validationForm(true)"
                >
                  <feather-icon icon="SaveIcon" class="icon-button" size="16" />
                  {{ $t('Save as draft') }}
                </b-button>

                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  type="submit"
                  class="mr-2 mt-1 publish-button"
                  :disabled="submitLoader"
                  @click.prevent="validationForm(false)"
                >
                  <feather-icon icon="SendIcon" class="icon-button" size="16" />
                  {{ $t('Publish') }}
                </b-button>
                <b-button
                  v-if="itemId !== -1"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="danger"
                  class="mt-1 delete-button"
                  type="submit"
                  @click.prevent="$router.go(-1)"
                >
                  <feather-icon icon="Trash2Icon" class="icon-button" size="16" />
                  {{ itemId !== -1 ? $t('Delete') : $t('Discard') }}
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BFormInput,
  BFormGroup,
  BForm,
  BFormCheckbox,
  BRow,
  BCol,
  BButton,
  BFormSelect,
  BLink,
  BTab,
  BTabs,
  BTooltip,
} from 'bootstrap-vue'
import { required } from '@/libs/validations'
import vSelect from 'vue-select'
import VueSlider from 'vue-slider-component'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'

import { quillEditor } from 'vue-quill-editor'
import DropZone from '@/components/DropZone'
import ActivityService from '@/services/ActivityService'
import activityType from '@/views/pages/activities/activityType'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import ActivityScheduleOptions from '@/components/ActivityScheduleOptions'
import PriceOptions from '@/components/PriceOptions'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@/components/ToastificationContent'
import Modal from '@/components/Modal'
import userCan from '@/libs/user-can'
import { editorConfig } from '@/libs/editor-config'

export default {
  name: 'ActivitiesForm',
  components: {
    Modal,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    VueSlider,
    quillEditor,
    BFormInput,
    BFormGroup,
    BFormCheckbox,
    BForm,
    BTooltip,
    BRow,
    BCol,
    BButton,
    DropZone,
    DatePicker,
    ActivityScheduleOptions,
    PriceOptions,
    BFormSelect,
    BLink,
    BTab,
    BTabs,
  },
  directives: {
    Ripple,
  },
  props: {
    formTitle: {
      type: String,
      default: '',
      required: true,
    },
    typeName: {
      type: String,
      default: '',
      required: true,
    },
    typeId: {
      type: Number,
      required: true,
    },
    itemId: {
      type: Number,
      default: -1,
    },
    formData: {
      type: Object,
      default: () => {
        return {
          name: '',
          min_age: 4,
          max_age: 11,
          from_date: null,
          to_date: null,
          price_options: [],
          days: [],
          status: 'published',
          location_id: '',
          user_id: '',
          language_options: [],
          category_options: [],
          details: undefined,
          keywords: '',
          billable: false,
          files: [],
          filesThumbnail: [],
        }
      },
    },
  },

  data() {
    return {
      userLang: this.$store.state.appConfig.getLang(),
      userCan: userCan,
      modalOptions: {
        text: '',
        okButton: '',
        cancelButton: '',
        clickedId: 0,
        currentAction: '',
      },
      show: false,
      skipOnceInEditPage: this.itemId !== -1,
      hasChanged: false,
      priceChange: false,
      scheduleChange: false,
      initFormStat: JSON.parse(JSON.stringify(this.formData)),
      editorOptions: editorConfig,
      ActivityService: new ActivityService(),
      required,
      marks: [0, 0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19],
      locationsList: [],
      daysList: [],
      providersList: [],
      languagesList: [],
      classTypeList: [],
      classTypeLoader: true,
      locationLoader: true,
      providerLoader: true,
      languageLoader: true,
      submitLoader: false,
      defaultPriceData: {
        id: -1,
        price: '',
        price_unit: 'Hour',
        price_info: '',
      },
      defaultDaysData: { id: -1, day_id: null, from_time: null, to_time: null },
    }
  },
  computed: {
    ageRange: {
      get() {
        return [this.formData.min_age, this.formData.max_age]
      },
      set(newName) {
        this.formData.min_age = newName[0]
        this.formData.max_age = newName[1]
        return [this.formData.min_age, this.formData.max_age]
      },
    },
    filesInput() {
      return this.formData.files?.length ? 'dummy text' : ''
    },
    fromTime: {
      get() {
        let from = this.formData.from_time ? this.formData.from_time.split(':') : null
        if (from)
          if (+from[0] > 12) {
            from =
              (+from[0] - 12 > 9 ? +from[0] - 12 : '0' + (+from[0] - 12).toString()) +
              ':' +
              from[1] +
              ' pm'
          } else {
            from = (+from[0] > 9 ? +from[0] : '0' + (+from[0]).toString()) + ':' + from[1] + ' am'
          }
        return from
      },
      set(newTime) {
        if (newTime) {
          let convertedDate = newTime.split(' ')
          if (newTime.includes('pm')) {
            convertedDate = convertedDate[0].split(':')
            convertedDate = +convertedDate[0] + 12 + ':' + convertedDate[1]
          } else {
            convertedDate = convertedDate[0].split(':')
            convertedDate = +convertedDate[0] + ':' + convertedDate[1]
          }
          this.formData.from_time = convertedDate
        } else {
          this.formData.from_time = null
        }
        return newTime
      },
    },
    toTime: {
      get() {
        let to = this.formData.to_time ? this.formData.to_time.split(':') : null
        if (to)
          if (+to[0] > 12) {
            to =
              (+to[0] - 12 > 9 ? +to[0] - 12 : '0' + (+to[0] - 12).toString()) + ':' + to[1] + ' pm'
          } else {
            to = (+to[0] > 9 ? +to[0] : '0' + (+to[0]).toString()) + ':' + to[1] + ' am'
          }
        return to
      },
      set(newTime) {
        if (newTime) {
          let convertedDate = newTime.split(' ')
          if (newTime.includes('pm')) {
            convertedDate = convertedDate[0].split(':')
            convertedDate = +convertedDate[0] + 12 + ':' + convertedDate[1]
          } else {
            convertedDate = convertedDate[0].split(':')
            convertedDate = +convertedDate[0] + ':' + convertedDate[1]
          }
          this.formData.to_time = convertedDate
        } else {
          this.formData.to_time = null
        }
        return newTime
      },
    },
  },
  watch: {
    'formData.days': {
      handler(val, old) {
        this.formData.days = val
      },
      deep: true,
    },
    formData: {
      handler(val, old) {
        if (this.priceChange || this.scheduleChange) {
          this.$emit('formChanged', true)
          this.hasChanged = true
          return
        }
        const formChanged = Object.keys(this.formData).some(field => {
          if (Array.isArray(this.initFormStat[field])) {
            return this.formData[field].length !== this.initFormStat[field].length
          } else {
            return this.formData[field] !== this.initFormStat[field]
          }
        })
        this.$emit('formChanged', formChanged)
        this.hasChanged = formChanged
      },
      deep: true,
    },
  },
  mounted() {
    this.getLocationsList()
    this.getWeekDayList()
    this.getLanguagesList()
    this.getCategoriesList()
    if (!this.formData.days.length) {
      this.addNewScheduleOption()
    }
  },
  methods: {
    disableDateBefore(date) {
      if (this.formData.from_date) {
        const splitDate = this.formData.from_date.split('/')
        const from = new Date(splitDate[2], +splitDate[1] - 1, splitDate[0])
        from.setHours(0, 0, 0, 0)
        return date < from
      }
    },
    disableDateAfter(date) {
      if (this.formData.to_date) {
        const splitDate = this.formData.to_date.split('/')
        const to = new Date(splitDate[2], +splitDate[1] - 1, splitDate[0])
        to.setHours(0, 0, 0, 0)
        return date > to
      }
    },
    addNewPriceOption() {
      this.formData.price_options.push({ ...this.defaultPriceData })
      if (!this.initFormStat.price_options.length) {
        this.initFormStat.price_options.push({ ...this.defaultPriceData })
      }
      const index = 'priceOption' + (this.formData.price_options.length - 1)
      setTimeout(() => {
        this.$refs[index][0].$el.getElementsByClassName('editOptionFromParent')[0].click()
      }, 50)
    },
    changePriceData(event, price) {
      price.price = event.price
      price.price_info = event.price_info
      if (event.price_unit != '') {
        price.price_unit = event.price_unit
      } else {
        price.price_unit = event.price_custom
      }
      price.pricing_model = event.pricing_model
      price.billable = event.billable
      price.display_price = `${price.price / 100} €`
      this.priceChange = true
    },
    deletePriceOption(index) {
      this.formData.price_options.splice(index, 1)
      this.priceChange = true
    },
    addNewScheduleOption() {
      this.formData.days.push({ ...this.defaultDaysData })
      if (!this.initFormStat.days.length) {
        this.initFormStat.days.push({ ...this.defaultDaysData })
      }
      const index = 'ScheduleOption' + (this.formData.days.length - 1)
      setTimeout(() => {
        this.$refs[index][0].$el.getElementsByClassName('editOptionFromParent')[0].click()
      }, 50)
    },
    changeScheduleData(data, option) {
      option.day_id = data.day_id
      option.from_time = data.from_time
      option.to_time = data.to_time
      this.scheduleChange = true
    },
    deleteScheduleOption(index) {
      this.formData.days.splice(index, 1)
      this.scheduleChange = true
    },
    getLocationsList() {
      this.locationLoader = true
      this.ActivityService.getLocationsList().then(res => {
        this.locationLoader = false
        this.locationsList = res.data
      })
    },
    getWeekDayList() {
      this.ActivityService.getWeekDayList().then(res => {
        this.daysList = res.data
      })
    },
    getLanguagesList() {
      this.languageLoader = true
      this.ActivityService.getLanguagesList().then(res => {
        this.languageLoader = false
        this.languagesList = res.data
      })
    },
    getCategoriesList() {
      this.classTypeLoader = true
      this.ActivityService.getCategoriesList().then(res => {
        this.classTypeLoader = false
        this.classTypeList = res.data
      })
    },
    changeFilesIDs(e) {
      this.formData.files = e
    },
    validationForm(draft) {
      if (draft) {
        this.draft(this.formData)
        return
      }
      this.$refs.simpleRules.validate().then(success => {
        if (success) {
          const hasEditingSchedules = Boolean(
            document.getElementById('check-schedule-options').getElementsByClassName('editMode')
              .length
          )
          const hasEditingPrices = Boolean(
            document.getElementById('check-price-options').getElementsByClassName('editMode').length
          )
          if (hasEditingSchedules || hasEditingPrices) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('Error'),
                icon: 'AlertTriangleIcon',
                text: this.$t(
                  hasEditingSchedules ? 'non_saved_schedules_error' : 'non_saved_prices_error'
                ),
                variant: 'danger',
              },
            })

            return
          }

          this.submit(this.formData)
        }
      })
    },
    submit(form) {
      this.submitLoader = true
      form.type_id = +this.typeId
      // if itemId passed from parent that means the user in the edit page.
      const formKeysOnly = Object.assign({}, form)
      // remove unused keys before sent request
      delete formKeysOnly.filesThumbnail
      formKeysOnly.price_options = formKeysOnly.price_options.filter(option => {
        option.id = undefined
        return option.price !== ''
      })
      formKeysOnly.days = formKeysOnly.days.filter(option => {
        option.id = option.id === -1 ? undefined : option.id
        return option.day_id !== ''
      })
      formKeysOnly.status = 'published'
      // return
      this.sendApi(formKeysOnly)
    },
    draft(form = this.formData) {
      this.submitLoader = true
      form.type_id = +this.typeId
      // if itemId passed from parent that means the user in the edit page.
      const formKeysOnly = Object.assign({}, form)
      // remove unused keys before sent request
      delete formKeysOnly.filesThumbnail
      formKeysOnly.price_options = formKeysOnly.price_options.filter(option => {
        option.id = undefined
        return option.price !== ''
      })
      formKeysOnly.days = formKeysOnly.days.filter(option => {
        option.id = option.id === -1 ? undefined : option.id
        return option.day !== ''
      })
      formKeysOnly.status = 'draft'
      this.sendApi(formKeysOnly)
    },
    sendApi(formData) {
      if (this.itemId === -1) {
        this.$posthog.capture('Activity created', { name: formData.name })
        this.ActivityService.create(formData).then(
          res => {
            this.submitLoader = false
            this.$emit('formChanged', false)
            // when user save form as draft by confirm modal action
            if (this.modalOptions.goToPath) {
              this.$router.push({ name: this.modalOptions.goToPath })
              return
            }
            switch (+this.typeId) {
              case activityType.ACTIVITY:
                this.$router.push({ name: 'activities' })
                break
              case activityType.CAMP:
                this.$router.push({ name: 'camps' })
                break
              case activityType.EVENT:
                this.$router.push({ name: 'events' })
                break
            }
          },
          error => {
            this.submitLoader = false
          }
        )
      } else {
        this.$posthog.capture('Activity updated', { name: formData.name })
        this.ActivityService.update(this.itemId, formData).then(
          res => {
            this.submitLoader = false
            this.$emit('formChanged', false)
            switch (+this.typeId) {
              case activityType.ACTIVITY:
                this.$router.push({ name: 'activities' })
                break
              case activityType.CAMP:
                this.$router.push({ name: 'camps' })
                break
              case activityType.EVENT:
                this.$router.push({ name: 'events' })
                break
            }
          },
          error => {
            this.submitLoader = false
            console.log(error)
          }
        )
      }
    },
    modalResult(value) {
      this.show = false
      // to stop the before route leave validation in the create activity component
      this.$emit('formChanged', false)
      if (value) {
        this.draft(this.formData)
      } else {
        this.$router.push({ name: this.modalOptions.goToPath })
      }
    },
  },
}
</script>
<style lang="scss">
.ql-editor {
  min-height: 100px;
}

.custom-form-group > div,
.custom-form-group > div > span {
  display: contents;
}

.custom-form-group > label {
  margin-left: auto;
}

.custom-error-style {
  right: 0;
  bottom: -1.5rem;
}

.option-label > label {
  //color: var(--primary);
  font-weight: 600;
  font-size: 20px;
}

.custom-style {
  width: 60%;
}

.description-col .tab-content {
  margin-top: 0.5em !important;
}

.description-col small {
  margin-bottom: 1em;
  display: block;
}
</style>
